// Onboarding status — the single, derived source of the Commerce setup state.
// (COMMERCE_ONBOARDING_PHASE1_TICKET_2026-09-17.md, ONBOARDING_EXPERIENCE.md §2.)
// State is DERIVED from live workspace data, never stored flags — only the
// per-tenant "dismissed" pointer persists. Combines GET /v1/tracking/status
// (tracker traffic, events received, contacts) with the feed schedules
// (products / customers / sales / GA4 history) into the 7 core steps + a count.
// Cached briefly so the gate, the Setup nav badge and the checklist share one read.

import ServiceFactory from "../service";
import account from "../data/account";
import { journeySvc } from "../views/email-journeys/common";
import { hasTenantFeature } from "./tenant-features";
const trk = api => {
  const S = ServiceFactory.getType(`/tracking${api}`, "product");
  return new S();
};
const feeds = api => {
  const S = ServiceFactory.getType(`/data-feeds${api}`, "product");
  return new S();
};
const safe = async (p, f) => {
  try {
    return (await p) || f;
  } catch (e) {
    return f;
  }
};
const CORE = ["company", "tracker", "ga4", "events", "products", "customers", "sales"];
const TTL = 60 * 1000;
let _cache = null; // { at, data }

const dismissKey = () => `plunk-onboarding-dismissed:${(account.get() || {}).tenantId || "ws"}`;
export function isOnboardingDismissed() {
  try {
    return localStorage.getItem(dismissKey()) === "1";
  } catch (e) {
    return false;
  }
}
export function dismissOnboarding() {
  try {
    localStorage.setItem(dismissKey(), "1");
  } catch (e) {/* private mode */}
}
export function clearOnboardingCache() {
  _cache = null;
}

// "Mark setup complete" (user 2026-09-27): an explicit, per-workspace mark that closes the
// checklist regardless of which steps derive done — the Setup menu entry and the dashboard
// strip go away, the Setup page shows Complete. Per browser (localStorage, like the email
// step and the dismiss above); "Reopen setup" on the Setup page clears it.
const completeKey = () => `plunk-setup-complete:${(account.get() || {}).tenantId || "ws"}`;
export function isSetupMarkedComplete() {
  try {
    return localStorage.getItem(completeKey()) === "1";
  } catch (e) {
    return false;
  }
}
export function markSetupComplete(on) {
  try {
    if (on === false) localStorage.removeItem(completeKey());else localStorage.setItem(completeKey(), "1");
  } catch (e) {/* private mode */}
  _cache = null;
}

// Returns { configured, steps{7 bools}, order[], done, total, next, raw }.
// Never throws — a failed read reads as "nothing connected yet".
export async function getOnboardingStatus(force) {
  if (!force && _cache && Date.now() - _cache.at < TTL) return _cache.data;
  const [status, schedules, journeys] = await Promise.all([safe(trk("/status").getAsync(), null), safe(feeds("/schedules").getAsync(), []), safe(journeySvc().getAsync(), null)]);
  // "Bring your email over" (Setup/Email): done once any journey exists, or the merchant
  // created/skipped from that page (localStorage — per browser, like other setup hints).
  let emailDone = Array.isArray(journeys) ? journeys.length > 0 : !!(journeys && Array.isArray(journeys.journeys) && journeys.journeys.length);
  try {
    const a = account.get() || {};
    const raw = localStorage.getItem(`plunk.emailImport.${a.tenantId || a.account || "t"}`);
    if (raw && /created|skipped/.test(raw)) emailDone = true;
  } catch (e) {/* no storage */}
  const activeFeed = provider => (schedules || []).some(s => s.provider === provider && s.status === "Active");
  const anyFeed = provider => (schedules || []).some(s => s.provider === provider);
  const trackerLive = !!(status && status.tracker && status.tracker.hasData);
  // Shopify: the theme embed is required; the app pixel alone does not complete the step
  // (GET /v1/tracking/status → tracker.embed.hasData, 2026-10-01).
  const embedLive = !!(status && status.tracker && status.tracker.embed && status.tracker.embed.hasData);
  const eventsLive = !!(status && status.conversions && status.conversions.hasData);
  const hasContacts = !!(status && status.contacts && status.contacts.count > 0);
  const acct = account.get() || {};

  // Shopify tenants (feature flag, or a Shopify connection already exists) collapse
  // the three commerce steps into ONE "Connect your Shopify store" step (§178/§179).
  let shopify = false;
  try {
    shopify = hasTenantFeature("shopify");
  } catch (e) {/* default false */}
  shopify = shopify || anyFeed("Shopify");
  const shopifyConnected = activeFeed("Shopify");
  // The connected store's domain (from the Shopify schedule's settings) — the plan gate and
  // the Setup tracker step (theme-editor deep link) need it.
  let shop = "";
  try {
    const sched = (schedules || []).find(s => s.provider === "Shopify");
    const st = sched && sched.settings ? typeof sched.settings === "string" ? JSON.parse(sched.settings) : sched.settings : null;
    shop = (st && (st.ShopifyShopDomain || st.shopifyShopDomain) || "").trim().toLowerCase();
  } catch (e) {
    shop = "";
  }
  const steps = {
    company: !!acct.domain,
    // website confirmed at signup/provisioning
    tracker: shopify ? trackerLive && embedLive : trackerLive,
    // live traffic (Shopify: from the theme embed)
    ga4: anyFeed("HistoricalImport"),
    // one-time history import registered
    events: eventsLive,
    // key-action events received
    products: activeFeed("Product"),
    customers: hasContacts || activeFeed("Customer"),
    // auto-builds from tracking, or a feed
    sales: activeFeed("SalesTransaction"),
    shopify: shopifyConnected,
    // one connection covers products+customers+sales
    email: emailDone // optional: email automations brought over (Setup/Email)
  };
  // The ordered core the checklist walks — commerce steps collapse for Shopify.
  const order = shopify
  // GA4 history is optional for a Shopify store (its order history comes with the
  // connection) — it lives in "tune your workspace", not the core count (2026-09-27).
  ? ["company", "tracker", "events", "shopify", "email"] : [...CORE, "email"];
  const marked = isSetupMarkedComplete();
  const done = marked ? order.length : order.filter(k => steps[k]).length;
  const next = marked ? null : order.find(k => !steps[k]) || null;
  const data = {
    configured: !!(status && status.configured) || marked,
    // §177 gate: any signal → not "fresh"
    shopify,
    shop,
    steps,
    order,
    done,
    total: order.length,
    next,
    marked,
    raw: status
  };
  _cache = {
    at: Date.now(),
    data
  };
  return data;
}

// Theme app extension deep link (ShopifyService/shopify-app/extensions/plunk-tracker): the "Plunk" app embed block,
// installed at store connect. Shopify's form is activateAppId={app client_id}/{block handle}; the extension-uid form
// is deprecated and the editor rejects it (verified 2026-09-26). Shared by Setup and the Tracking page (2026-11-03).
export const PLUNK_EMBED_ID = "c770efdf550be03f2f9760498325be77/plunk-tracker";
export function shopifyThemeEmbedUrl(shop) {
  const store = String(shop || "").replace(/\.myshopify\.com$/i, "");
  return store ? `https://admin.shopify.com/store/${encodeURIComponent(store)}/themes/current/editor?context=apps&activateAppId=${PLUNK_EMBED_ID}` : null;
}